'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useRealtimeShipments } from '@/hooks/useRealtimeShipments';
import { createClient } from '@/lib/supabase/client';

export default function OrderTracking() {
  const supabase = createClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);

  // Get authenticated user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || null);
    };
    getUser();
  }, []);

  // Real-time shipments subscription
  const { shipments: orders, loading } = useRealtimeShipments(userId);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-success/10 text-success';
      case 'out_for_delivery': case'out for delivery':
        return 'bg-accent/10 text-accent';
      case 'in_transit': case'in transit':
        return 'bg-warning/10 text-warning';
      case 'pending':
        return 'bg-muted text-muted-foreground';
      case 'delayed':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-heading font-semibold text-foreground">Active Orders</h2>
        </div>
        <div className="grid gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-xl shadow-card border border-border p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-muted rounded w-1/3" />
                <div className="h-4 bg-muted rounded w-1/2" />
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="h-16 bg-muted rounded" />
                  <div className="h-16 bg-muted rounded" />
                  <div className="h-16 bg-muted rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-heading font-semibold text-foreground">
          Active Orders ({orders?.length || 0})
        </h2>
      </div>

      {/* Orders Grid */}
      {orders?.length > 0 ? (
        <div className="grid gap-6">
          {orders.map((order) => {
            const orderNumber = `ORD-${order.trackingNumber.slice(-8)}`;
            const formattedStatus = formatStatus(order.status);
            
            return (
              <div key={order.id} className="bg-card rounded-xl shadow-card border border-border">
                {/* Order Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">{orderNumber}</h3>
                      <p className="text-sm text-muted-foreground font-mono">{order.trackingNumber}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                      {formattedStatus}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <Icon name="MapPinIcon" size={16} />
                        <span>Current Location</span>
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {order.currentLocation || order.origin}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <Icon name="CalendarIcon" size={16} />
                        <span>Order Date</span>
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {new Date(order.orderDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <Icon name="ClockIcon" size={16} />
                        <span>Est. Delivery</span>
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {order.estimatedDelivery
                          ? new Date(order.estimatedDelivery).toLocaleDateString()
                          : 'TBD'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border flex gap-3">
                    <button
                      onClick={() => setSelectedOrder(selectedOrder === order.id ? null : order.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      <Icon name={selectedOrder === order.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} />
                      {selectedOrder === order.id ? 'Hide' : 'View'} Details
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors">
                      <Icon name="BellIcon" size={16} />
                      Set Alert
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedOrder === order.id && (
                  <div className="px-6 pb-6 pt-0 border-t border-border animate-fade-in">
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-foreground mb-4">Shipment Details</h4>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Origin:</span>
                          <span className="ml-2 font-medium text-foreground">{order.origin}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Destination:</span>
                          <span className="ml-2 font-medium text-foreground">{order.destination}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Weight:</span>
                          <span className="ml-2 font-medium text-foreground">{order.weight || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Dimensions:</span>
                          <span className="ml-2 font-medium text-foreground">{order.dimensions || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-card rounded-xl p-12 text-center border border-border">
          <Icon name="MapPinIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No active orders</p>
        </div>
      )}
    </div>
  );
}